// Shared option lists and label helpers for component schemas.

const BOOTSTRAP_FONT_SIZES_PX = [
    16, 18, 20, 22, 24, 26, 28, 30, 32, 36,
];

const titleCase = (s: string) =>
    s
        .split(/[\s-]+/g)
        .filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");

const labelForColorLikeClass = (raw: string) => {
    // raw example: primary-100, gray-200, body, white, etc.
    return raw
        .split("-")
        .map((part, idx) => (idx === 0 ? titleCase(part) : part))
        .join(" ");
};

const labelForTextClass = (cls: string) => {
    if (cls === "text-muted") return "Muted";
    if (cls === "text-body-secondary") return "Body secondary";
    const m = cls.match(/^text-(.+)$/);
    return m ? labelForColorLikeClass(m[1]) : cls;
};

const labelForAlignClass = (cls: string) => {
    if (cls === "text-start") return "Left";
    if (cls === "text-center") return "Center";
    if (cls === "text-end") return "Right";
    return cls;
};

export const fontSizeClassOptions = [
    { value: "no-class", label: "Default" },
    ...BOOTSTRAP_FONT_SIZES_PX.map((n) => ({ value: `fs-${n}`, label: `${n}px` })),
];

export const fontWeightClassOptions = [
    { value: "no-class", label: "Default" },
    { value: "fw-thin", label: "Thin" },
    { value: "fw-extralight", label: "Extra light" },
    { value: "fw-light", label: "Light" },
    { value: "fw-normal", label: "Normal" },
    { value: "fw-medium", label: "Medium" },
    { value: "fw-semibold", label: "Semibold" },
    { value: "fw-bold", label: "Bold" },
    { value: "fw-extrabold", label: "Extra bold" },
    { value: "fw-black", label: "Black" },
];

export const backgroundClassOptions = [
    // Standard colors first
    { value: "bg-transparent", label: "Transparent" },
    { value: "text-bg-white", label: "White" },
    { value: "text-bg-black", label: "Black" },
    { value: "text-bg-primary", label: "Primary" },
    { value: "text-bg-secondary", label: "Secondary" },
    { value: "text-bg-success", label: "Success" },
    { value: "text-bg-info", label: "Info" },
    { value: "text-bg-warning", label: "Warning" },
    { value: "text-bg-danger", label: "Danger" },

    // Primary scale
    ...[100, 200, 300, 400, 500, 600, 700, 800, 900].map((n) => ({
        value: `text-bg-primary-${n}`,
        label: `Primary ${n}`,
    })),
    // Secondary scale
    ...[100, 200, 300, 400, 500, 600, 700, 800, 900].map((n) => ({
        value: `text-bg-secondary-${n}`,
        label: `Secondary ${n}`,
    })),
];

export const borderWidthClassOptions = [
    { value: "no-class", label: "Default" },
    { value: "border border-0", label: "0" },
    { value: "border border-1", label: "1" },
    { value: "border border-3", label: "3" },
    { value: "border border-4", label: "4" },
    { value: "border border-5", label: "5" },
];

export const borderColorClassOptions = [
    { value: "no-class", label: "Default" },
    // Standard colors
    { value: "border-white", label: "White" },
    { value: "border-black", label: "Black" },
    { value: "border-primary", label: "Primary" },
    { value: "border-secondary", label: "Secondary" },
    { value: "border-success", label: "Success" },
    { value: "border-info", label: "Info" },
    { value: "border-warning", label: "Warning" },
    { value: "border-danger", label: "Danger" },
    // Primary scale
    ...[100, 200, 300, 400, 500, 600, 700, 800, 900].map((n) => ({ value: `border-primary-${n}`, label: `Primary ${n}` })),
    // Secondary scale
    ...[100, 200, 300, 400, 500, 600, 700, 800, 900].map((n) => ({ value: `border-secondary-${n}`, label: `Secondary ${n}` })),
];

export const paddingAllClassOptions = [
    { value: "no-class", label: "Default" },
    { value: "p-0", label: "0" },
    { value: "p-1", label: "1 (0.25rem)" },
    { value: "p-2", label: "2 (0.5rem)" },
    { value: "p-3", label: "3 (1rem)" },
    { value: "p-4", label: "4 (1.5rem)" },
    { value: "p-5", label: "5 (3rem)" },
];

export const innerGapClassOptions = [
    { value: "no-class", label: "Default" },
    { value: "gap-0", label: "0" },
    { value: "gap-1", label: "1 (0.25rem)" },
    { value: "gap-2", label: "2 (0.5rem)" },
    { value: "gap-3", label: "3 (1rem)" },
    { value: "gap-4", label: "4 (1.5rem)" },
    { value: "gap-5", label: "5 (3rem)" },
];

export const roundedClassOptions = [
    { value: "no-class", label: "None" },
    { value: "rounded", label: "Default" },
    { value: "rounded-0", label: "Rounded 0" },
    { value: "rounded-1", label: "Rounded 1" },
    { value: "rounded-2", label: "Rounded 2" },
    { value: "rounded-3", label: "Rounded 3" },
    { value: "rounded-4", label: "Rounded 4" },
    { value: "rounded-5", label: "Rounded 5" },
    { value: "rounded-pill", label: "Pill" },
    { value: "rounded-top", label: "Top" },
    { value: "rounded-end", label: "Bottom" },
];

export const shadowClassOptions = [
    { value: "no-class", label: "None" },
    { value: "shadow-sm", label: "Small" },
    { value: "shadow", label: "Medium" },
    { value: "shadow-lg", label: "Large" },
    { value: "shadow-none", label: "None (explicit)" },
];

export const textClassOptions = [
    { value: "no-class", label: "Default" },
    // Standard colors
    { value: "text-white", label: "White" },
    { value: "text-black", label: "Black" },
    { value: "text-primary", label: "Primary" },
    { value: "text-secondary", label: "Secondary" },
    { value: "text-success", label: "Success" },
    { value: "text-info", label: "Info" },
    { value: "text-warning", label: "Warning" },
    { value: "text-danger", label: "Danger" },

    // Primary scale
    ...[100, 200, 300, 400, 500, 600, 700, 800, 900].map((n) => ({
        value: `text-primary-${n}`,
        label: `Primary ${n}`,
    })),
    // Secondary scale
    ...[100, 200, 300, 400, 500, 600, 700, 800, 900].map((n) => ({
        value: `text-secondary-${n}`,
        label: `Secondary ${n}`,
    })),
];

export const textAlignClassOptions = [
    { value: "no-class", label: "Default" },
    ...["text-start", "text-center", "text-end"].map((cls) => ({ value: cls, label: labelForAlignClass(cls) })),
];

export const blockAlignClassOptions = [
    { value: "no-class", label: "Default" },
    { value: "me-auto", label: "Left" },
    { value: "mx-auto", label: "Center" },
    { value: "ms-auto", label: "Right" },
];

export const sectionContentAlignClassOptions = [
    { value: "align-items-start text-start", label: "Left" },
    { value: "align-items-center text-center", label: "Center" },
    { value: "align-items-end text-end", label: "Right" },
];

export const sectionContentJustifyClassOptions = [
    { value: "justify-content-start", label: "Top" },
    { value: "justify-content-center", label: "Center" },
    { value: "justify-content-end", label: "Bottom" },
];

export const gutterClassOptions = [
    { value: "no-class", label: "None" },
    { value: "g-0", label: "0" },
    { value: "g-1", label: "1 (0.25rem)" },
    { value: "g-2", label: "2 (0.5rem)" },
    { value: "g-3", label: "3 (1rem)" },
    { value: "g-4", label: "4 (1.5rem)" },
    { value: "g-5", label: "5 (3rem)" },
];

export const marginYClassOptions = [
    { value: "no-class", label: "None" },
    ...[0, 1, 2, 3, 4, 5].map((n) => ({ value: `my-${n}`, label: `${n}` })),
];

export const marginXClassOptions = [
    { value: "no-class", label: "None" },
    ...[0, 1, 2, 3, 4, 5].map((n) => ({ value: `mx-${n}`, label: `${n}` })),
];

export const buttonColorClassOptions = [
    // Standard colors
    { value: "white", label: "White" },
    { value: "black", label: "Black" },
    { value: "primary", label: "Primary" },
    { value: "secondary", label: "Secondary" },
    { value: "success", label: "Success" },
    { value: "info", label: "Info" },
    { value: "warning", label: "Warning" },
    { value: "danger", label: "Danger" },

    // Primary scale
    ...[100, 200, 300, 400, 500, 600, 700, 800, 900].map((n) => ({
        value: `primary-${n}`,
        label: `Primary ${n}`,
    })),

    // Secondary scale
    ...[100, 200, 300, 400, 500, 600, 700, 800, 900].map((n) => ({
        value: `secondary-${n}`,
        label: `Secondary ${n}`,
    })),
];
